import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "清晰定位",
    desc: "围绕漫画阅读中最常见的六类问题展开，不堆砌无关内容，让你一来就能找到自己遇到的状况。",
  },
  {
    icon: "ri-road-map-line",
    title: "步骤化解决",
    desc: "把每个问题拆解成由易到难的排查步骤，从检查网络到清理缓存，逐步操作即可。",
  },
  {
    icon: "ri-shield-check-line",
    title: "简单可靠",
    desc: "用口语化的表达写说明，不绕弯子，确保非技术读者也能看懂、照着做、解决问题。",
  },
  {
    icon: "ri-group-line",
    title: "持续更新",
    desc: "根据读者的真实反馈不断补充和修订内容，让帮助中心始终贴合大家最关心的使用痛点。",
  },
];

const principles = [
  {
    num: "01",
    title: "先易后难",
    desc: "排查步骤从最简单的操作开始，多数问题无需折腾，第一步往往就能解决。",
  },
  {
    num: "02",
    title: "覆盖全面",
    desc: "从打不开、加载慢到收藏、搜索、登录，几乎覆盖日常使用的所有高频问题。",
  },
  {
    num: "03",
    title: "人话表述",
    desc: "避免专业术语堆砌，用读者熟悉的说法讲清楚，操作起来毫不费力。",
  },
  {
    num: "04",
    title: "即时反馈",
    desc: "内容根据真实使用场景持续打磨，遇到新问题我们会尽快补充对应方案。",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于笔笔漫画网帮助中心",
  url: "https://manhua4.github.io/about",
  description:
    "笔笔漫画网帮助中心是面向漫画阅读用户的自助问题解决平台，提供打不开、加载慢、换源、收藏、搜索、登录等常见使用问题的排查方案。",
};

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 - 笔笔漫画网帮助中心"
        description="了解笔笔漫画网帮助中心的定位与理念：专注解决漫画阅读中的打不开、加载慢、换源失败、收藏丢失、搜索无结果、登录异常等常见使用问题，提供清晰步骤化的自助排查方案。"
        keywords="笔笔漫画网,关于我们,帮助中心,使用问题解决,漫画阅读帮助"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="inline-block rounded-full bg-primary-100 px-4 py-1 text-sm font-medium text-primary-700">
                  关于我们
                </span>
                <h1 className="mt-4 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                  让每一次漫画阅读，
                  <br />
                  都顺畅发生
                </h1>
                <p className="mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
                  笔笔漫画网帮助中心是一个专门为漫画读者打造的<a href="#guide" className="text-primary-600 underline decoration-primary-200 underline-offset-2">自助问题解决平台</a>。
                  我们深知追更时网页打不开、加载慢、换源失败有多扫兴，也明白收藏丢失、搜索无结果、登录异常带来的困扰。
                  因此我们把这些高频问题整理成清晰、可操作的排查步骤，让你自己动手就能恢复顺畅阅读。
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-home-4-line" />
                    回到首页
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    <i className="ri-chat-3-line" />
                    联系我们
                  </Link>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-3 -z-10 rotate-2 rounded-3xl bg-primary-100" />
                <img
                  src="https://readdy.ai/api/search-image?query=Cheerful%20flat%20illustration%20of%20people%20reading%20comics%20on%20laptop%20and%20phone%2C%20question%20marks%20and%20lightbulb%20icons%20floating%20around%2C%20warm%20coral%20orange%20teal%20and%20cream%20palette%2C%20playful%20cartoon%20style%2C%20soft%20clean%20background%2C%20cozy%20friendly%20atmosphere&width=1200&height=900&seq=bibi-about-01&orientation=landscape"
                  alt="漫画读者使用笔笔漫画网帮助中心解决问题"
                  className="aspect-[4/3] w-full rounded-3xl object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="guide" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-block rounded-full bg-secondary-100 px-4 py-1 text-sm font-medium text-secondary-900">
              我们坚持的理念
            </span>
            <h2 className="mt-4 text-2xl font-black text-foreground-950 md:text-3xl">
              四个原则，贯穿每一篇帮助内容
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-2xl border border-background-200 bg-background-50 p-6 transition-all hover:border-primary-200 hover:shadow-soft"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-700">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-5 text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-block rounded-full bg-accent-100 px-4 py-1 text-sm font-medium text-accent-900">
                内容原则
              </span>
              <h2 className="mt-4 text-2xl font-black text-foreground-950 md:text-3xl">
                我们如何打磨每一篇方案
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {principles.map((p) => (
                <div
                  key={p.num}
                  className="flex gap-5 rounded-2xl border border-background-200 bg-background-50 p-6"
                >
                  <span className="font-display text-4xl text-secondary-500">{p.num}</span>
                  <div>
                    <h3 className="text-lg font-bold text-foreground-950">{p.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-3 -z-10 -rotate-2 rounded-3xl bg-accent-100" />
              <img
                src="https://readdy.ai/api/search-image?query=Friendly%20customer%20support%20concept%20flat%20illustration%2C%20headset%20and%20chat%20bubbles%20with%20check%20marks%2C%20warm%20coral%20and%20teal%20accent%20colors%2C%20clean%20cream%20background%2C%20playful%20cartoon%20style%2C%20approachable%20and%20helpful%20mood&width=1200&height=900&seq=bibi-about-02&orientation=landscape"
                  alt="笔笔漫画网帮助中心贴心服务示意"
                  className="aspect-[4/3] w-full rounded-3xl object-cover object-top"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="inline-block rounded-full bg-primary-100 px-4 py-1 text-sm font-medium text-primary-700">
                  我们的承诺
                </span>
                <h2 className="mt-4 text-2xl font-black text-foreground-950 md:text-3xl">
                  你的阅读困扰，就是我们的工作重点
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
                  我们持续收集读者的反馈，把大家最常遇到的打不开、加载慢、换源、收藏、搜索、登录等问题，
                  整理成简单好用的解决方案。遇到新问题时，我们也会尽快补充对应内容，
                  让这个帮助中心始终贴合真实的使用场景。
                </p>
                <ul className="mt-6 space-y-3">
                  {["覆盖六大高频使用问题", "步骤清晰、人人都能照着做", "根据真实反馈持续更新"].map(
                    (t) => (
                      <li key={t} className="flex items-center gap-3 text-sm text-foreground-700">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-background-50">
                          <i className="ri-check-line text-sm" />
                        </span>
                        {t}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
        </section>

        <section className="bg-primary-500 py-14 md:py-16">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-6">
            <h2 className="text-2xl font-black text-background-50 md:text-3xl">
              有关于笔笔漫画网的使用问题？
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-primary-100 md:text-base">
              如果你遇到了帮助中心还没覆盖的问题，欢迎告诉我们，我们会认真对待每一条反馈。
            </p>
            <Link
              to="/contact"
              className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-mail-send-line" />
              给我们反馈
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}